import React from 'react';
import { useTranslation } from 'react-i18next';
import TaxCategorySelect from '../../../components/tax/TaxCategorySelect';
import TaxCategoryLabel from '../../../components/tax/TaxCategoryLabel';
import NetGrossField from '../../../components/pricing/NetGrossField';
import PriceEntryNote from '../../../components/pricing/PriceEntryNote';
import { usePriceEntryMode, useTaxRateOf } from '../../../hooks/usePriceEntry';
import { AssetAdditionalService, decimalToNumber } from '../../../api/asset/assetApiTypes';
import { FormService } from '../AssetDetailPage';
import AssetDetailSection from './AssetDetailSection';

interface AdditionalServicesSectionProps {
    services: AssetAdditionalService[];
    mode: 'view' | 'edit' | 'create';
    formServices?: FormService[];
    onServiceChange?: (serviceId: string, updates: Partial<FormService>) => void;
    onAddService?: () => void;
    onRemoveService?: (serviceId: string) => void;
}

/**
 * One add-on's unit price. Its own component because the VAT rate net entry
 * converts at is the row's own category (#2967), and a hook cannot run per row
 * inside the table's map.
 */
const UnitPriceInput = ({ unitPrice, taxCategoryId, onChange }: Readonly<{
    unitPrice: number;
    taxCategoryId: string;
    onChange: (unitPrice: number) => void;
}>) => {
    const rate = useTaxRateOf([taxCategoryId || null]);
    return (
        <NetGrossField
            gross={unitPrice.toString()}
            onGrossChange={(gross) => {
                const val = parseFloat(gross);
                onChange(Number.isNaN(val) ? 0 : val);
            }}
            rate={rate}
        >
            {(input) => (
                <input
                    type={'number'}
                    step={'0.01'}
                    min={'0'}
                    value={input.value}
                    onChange={(e) => input.onChange(e.target.value)}
                    className={'w-24 text-right border border-foreground/20'
                        + ' rounded px-2 py-1 text-sm outline-none focus:border-primary'}
                />
            )}
        </NetGrossField>
    );
};

const AdditionalServicesSection: React.FC<AdditionalServicesSectionProps> = ({
    services,
    mode,
    formServices,
    onServiceChange,
    onAddService,
    onRemoveService,
}) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language.startsWith('de') ? 'de' : 'en';
    const locale = lang === 'de' ? 'de-DE' : 'en-US';
    const isEditing = mode === 'edit' || mode === 'create';
    const priceEntry = usePriceEntryMode();

    const getLocalizedName = (service: AssetAdditionalService) =>
        service.langNames?.[lang] || service.serviceName;

    const handleLangNameChange = (svcId: string, langKey: string, value: string) => {
        const svc = formServices?.find((s) => s.id === svcId);
        if (!svc) return;
        onServiceChange?.(svcId, {
            langNames: { ...svc.langNames, [langKey]: value },
        });
    };

    const hasServices = services && services.length > 0;

    if (!isEditing && !hasServices) {
        return (
            <AssetDetailSection title={t('asset_detail.sections.additional_services')}>
                <p className={'text-sm text-foreground/40 italic'}>
                    {t('asset_detail.empty_states.additional_services')}
                </p>
            </AssetDetailSection>
        );
    }

    return (
        <AssetDetailSection title={t('asset_detail.sections.additional_services')}>
            {isEditing && <PriceEntryNote className={'text-xs text-foreground/60 mb-2'} />}
            <div className={'overflow-x-auto'}>
                <table className={'w-full text-sm'}>
                    <thead>
                        <tr className={'border-b border-foreground/10'}>
                            <th className={'text-left py-2 text-foreground/60 font-medium pr-4 whitespace-nowrap'}>
                                {t('asset_detail.fields.service_name')}
                            </th>
                            <th className={'text-right py-2 text-foreground/60 font-medium pr-4 whitespace-nowrap'}>
                                {isEditing && priceEntry === 'net'
                                    ? t('price_entry.net_label', { label: t('asset_detail.fields.unit_price') })
                                    : t('asset_detail.fields.unit_price')}
                            </th>
                            <th className={'text-left py-2 text-foreground/60 font-medium pr-4 whitespace-nowrap'}>
                                {t('asset_detail.fields.price_unit')}
                            </th>
                            <th className={'text-left py-2 text-foreground/60 font-medium pr-4 whitespace-nowrap'}>
                                {t('tax_category.label')}
                            </th>
                            <th className={'text-center py-2 text-foreground/60 font-medium pr-4 whitespace-nowrap'}>
                                {t('asset_detail.fields.mandatory')}
                            </th>
                            <th className={'text-center py-2 text-foreground/60 font-medium pr-4 whitespace-nowrap'}>
                                {t('asset_detail.fields.quantity_selectable')}
                            </th>
                            {isEditing && (
                                <th className={'w-8'} />
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {isEditing ? (
                            <>
                                {(formServices ?? []).map((svc) => (
                                    <tr key={svc.id} className={'border-b-2 border-foreground/10'}>
                                        <td className={'py-3 pr-4 min-w-[12rem]'}>
                                            <div className={'flex flex-col gap-1.5'}>
                                                <div className={'flex items-center gap-2'}>
                                                    <span className={'text-xs text-foreground/40 w-6'}>DE</span>
                                                    <input
                                                        type={'text'}
                                                        value={svc.langNames?.de ?? ''}
                                                        onChange={(e) => handleLangNameChange(svc.id, 'de', e.target.value)}
                                                        autoFocus={svc.isNew}
                                                        className={'flex-grow border border-foreground/20 rounded'
                                                            + ' px-2 py-1 text-sm outline-none focus:border-primary'}
                                                    />
                                                </div>
                                                <div className={'flex items-center gap-2'}>
                                                    <span className={'text-xs text-foreground/40 w-6'}>EN</span>
                                                    <input
                                                        type={'text'}
                                                        value={svc.langNames?.en ?? ''}
                                                        onChange={(e) => handleLangNameChange(svc.id, 'en', e.target.value)}
                                                        className={'flex-grow border border-foreground/20 rounded'
                                                            + ' px-2 py-1 text-sm outline-none focus:border-primary'}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                        <td className={'py-2 pr-4 text-right'}>
                                            <UnitPriceInput
                                                unitPrice={svc.unitPrice}
                                                taxCategoryId={svc.taxCategoryId}
                                                onChange={(unitPrice) => onServiceChange?.(svc.id, { unitPrice })}
                                            />
                                        </td>
                                        <td className={'py-2 pr-4 min-w-[9rem]'}>
                                            {/* #2873. per_person is deliberately
                                                absent: pricing cannot know the
                                                booked guest count yet, and the
                                                backend rejects it. */}
                                            <select
                                                value={svc.priceUnit ?? ''}
                                                onChange={(e) => onServiceChange?.(svc.id, {
                                                    priceUnit: e.target.value || null,
                                                })}
                                                className={'w-full border border-foreground/20 rounded'
                                                    + ' px-2 py-1 text-sm bg-background outline-none focus:border-primary'}
                                            >
                                                <option value={''}>{t('asset_detail.price_unit.per_piece')}</option>
                                                <option value={'per_booking'}>{t('asset_detail.price_unit.per_booking')}</option>
                                                <option value={'per_hour'}>{t('asset_detail.price_unit.per_hour')}</option>
                                                <option value={'per_day'}>{t('asset_detail.price_unit.per_day')}</option>
                                            </select>
                                        </td>
                                        <td className={'py-2 pr-4 min-w-[12rem]'}>
                                            {/* An add-on is taxed on its own,
                                                not the booking's (#1353) — an
                                                accommodation ancillary takes the
                                                standard rate even when the stay
                                                is reduced-rated. */}
                                            <TaxCategorySelect
                                                label={''}
                                                value={svc.taxCategoryId ?? ''}
                                                onChange={(value) => onServiceChange?.(svc.id, {
                                                    taxCategoryId: value,
                                                })}
                                            />
                                        </td>
                                        <td className={'py-2 pr-4 text-center'}>
                                            <button
                                                type={'button'}
                                                onClick={() => onServiceChange?.(svc.id, {
                                                    isPaymentCompulsory: !svc.isPaymentCompulsory,
                                                })}
                                                className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                                                    svc.isPaymentCompulsory
                                                        ? 'bg-primary/10 text-primary'
                                                        : 'bg-foreground/10 text-foreground/60'
                                                }`}
                                            >
                                                {svc.isPaymentCompulsory
                                                    ? t('asset_detail.yes')
                                                    : t('asset_detail.no')}
                                            </button>
                                        </td>
                                        <td className={'py-2 pr-4 text-center'}>
                                            <button
                                                type={'button'}
                                                onClick={() => onServiceChange?.(svc.id, {
                                                    quantity: !svc.quantity,
                                                })}
                                                className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                                                    svc.quantity
                                                        ? 'bg-primary/10 text-primary'
                                                        : 'bg-foreground/10 text-foreground/60'
                                                }`}
                                            >
                                                {svc.quantity
                                                    ? t('asset_detail.yes')
                                                    : t('asset_detail.no')}
                                            </button>
                                        </td>
                                        <td className={'py-2 text-center'}>
                                            <button
                                                type={'button'}
                                                onClick={() => onRemoveService?.(svc.id)}
                                                className={'text-foreground/40 hover:text-foreground text-lg px-1'}
                                                title={t('asset_detail.actions.remove')}
                                            >
                                                &times;
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </>
                        ) : (
                            services.map((service) => (
                                <tr key={service.id} className={'border-b border-foreground/5'}>
                                    <td className={'py-2 pr-4 text-foreground'}>
                                        {getLocalizedName(service)}
                                    </td>
                                    <td className={'py-2 pr-4 text-foreground text-right'}>
                                        {(decimalToNumber(service.unitPrice) ?? 0)
                                            .toLocaleString(locale, {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            })} EUR
                                    </td>
                                    <td className={'py-2 pr-4 text-foreground/70'}>
                                        {t(`asset_detail.price_unit.${service.priceUnit ?? 'per_piece'}`)}
                                    </td>
                                    <td className={'py-2 pr-4 text-foreground/70'}>
                                        {/* The header carries this column in
                                            both modes, so the read view shows
                                            the assignment rather than a gap
                                            that reads as "no tax". */}
                                        <TaxCategoryLabel
                                            taxCategoryId={service.taxCategoryId ?? null}
                                        />
                                    </td>
                                    <td className={'py-2 pr-4 text-center'}>
                                        {service.isPaymentCompulsory ? (
                                            <span className={'text-foreground/60'}>
                                                {t('asset_detail.yes')}
                                            </span>
                                        ) : (
                                            <span className={'text-foreground/40'}>
                                                {t('asset_detail.no')}
                                            </span>
                                        )}
                                    </td>
                                    <td className={'py-2 pr-4 text-center'}>
                                        {service.quantity ? (
                                            <span className={'text-foreground/60'}>
                                                {t('asset_detail.yes')}
                                            </span>
                                        ) : (
                                            <span className={'text-foreground/40'}>
                                                {t('asset_detail.no')}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            {isEditing && (
                <button
                    type={'button'}
                    onClick={onAddService}
                    className={'mt-3 text-sm text-primary hover:text-primary/80 font-medium'}
                >
                    {t('asset_detail.actions.add_service')}
                </button>
            )}
            {isEditing && (
                // #1735: a price change applies to future bookings only — existing
                // bookings keep the price they were made at. Stated here because
                // this is where the expectation is formed.
                <p className={'mt-3 text-sm text-foreground/60'}>
                    {t('asset_detail.additional_services.price_change_note')}
                </p>
            )}
        </AssetDetailSection>
    );
};

export default AdditionalServicesSection;
